import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Search, MapPin, Loader2, X, Check } from 'lucide-react';
import api from '@/lib/api';

/**
 * ClinicSearchPicker — autocomplete clinic name / address via our
 * server-proxied verified-place lookup and return a fully-structured place
 * to the parent form.
 *
 * Why proxy (not direct browser-to-provider)?
 *   - The provider token is held server-side → URL restrictions / fork URL
 *     changes don't affect us. One token, every fork.
 *   - We normalise the upstream payload into the exact shape the clinic
 *     form needs: { name, address, city, state, pincode, lat, lon, place_id }.
 *
 * Props
 *   - onPick(place): called when the doctor picks a result. Parent should
 *     populate its form fields from `place`.
 *   - placeholder: input placeholder text.
 *   - testid: optional data-testid prefix.
 */
export default function ClinicSearchPicker({ onPick, placeholder, testid = 'clinic-search' }) {
  const [q, setQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [error, setError] = useState('');
  const sessionRef = useRef(null);
  const boxRef = useRef(null);

  // Fresh session token per picker instance — groups all keystrokes into
  // a single upstream billable event.
  useEffect(() => {
    sessionRef.current = (window.crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random()}`);
  }, []);

  // Close dropdown when clicking outside.
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Debounced suggest fetch. 250 ms balances responsiveness against
  // billable API calls.
  useEffect(() => {
    if (!q || q.trim().length < 3 || picked) {
      setSuggestions([]); setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const r = await api.get('/clinics/search', {
          params: { q, session_token: sessionRef.current },
        });
        setSuggestions(r.data.suggestions || []);
        setOpen(true);
        setError('');
      } catch (e) {
        setError(e.response?.data?.detail || 'Search unavailable');
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [q, picked]);

  const handlePick = useCallback(async (sugg) => {
    setLoading(true); setOpen(false);
    try {
      const r = await api.get(`/clinics/search/retrieve/${sugg.place_id}`, {
        params: { session_token: sessionRef.current },
      });
      setPicked(r.data);
      onPick && onPick(r.data);
      setQ(r.data.name);
      // Issue a new session token — one session = one Retrieve (upstream
      // billing rule).
      sessionRef.current = (window.crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random()}`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load place details');
    } finally {
      setLoading(false);
    }
  }, [onPick]);

  const clear = () => {
    setQ(''); setPicked(null); setSuggestions([]); setOpen(false); setError('');
    onPick && onPick(null);
    sessionRef.current = (window.crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random()}`);
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testid}>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-9 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
          placeholder={placeholder || 'Search your clinic name or address…'}
          value={q}
          onChange={(e) => { setQ(e.target.value); setPicked(null); }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
        {loading && (
          <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
        )}
        {!loading && (picked || q) && (
          <button type="button" onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full"
            data-testid={`${testid}-clear`} aria-label="Clear">
            <X size={14} />
          </button>
        )}
      </div>

      {picked && (
        <div className="mt-2 rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2.5"
          data-testid={`${testid}-picked`}>
          <Check size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-emerald-900">{picked.name}</p>
            <p className="text-emerald-800 text-xs mt-0.5">
              {picked.full_address || [picked.address, picked.city, picked.pincode].filter(Boolean).join(', ')}
            </p>
            <p className="text-emerald-700 text-[11px] mt-1">
              📍 {picked.lat.toFixed(5)}, {picked.lon.toFixed(5)} — verified address
            </p>
          </div>
        </div>
      )}

      {open && suggestions.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-xl bg-white border border-slate-200 shadow-lg overflow-hidden max-h-80 overflow-y-auto"
          data-testid={`${testid}-dropdown`}>
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onClick={() => handlePick(s)}
              className="w-full text-left px-3 py-2.5 hover:bg-sky-50 transition-colors border-b border-slate-100 last:border-b-0 flex items-start gap-2.5"
              data-testid={`${testid}-option`}
            >
              <MapPin size={14} className="text-sky-500 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{s.name}</p>
                {s.full_address && (
                  <p className="text-xs text-slate-500 truncate">{s.full_address}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {error && !loading && (
        <p className="mt-1.5 text-xs text-rose-600" data-testid={`${testid}-error`}>{error}</p>
      )}
    </div>
  );
}
